import { useEffect, useMemo, useState } from 'react';
import {
  getAllUsers,
  getWatchBrands,
  type EntityRequest,
} from '../../services/firebase';
import { Modal } from '../common/Modal';
import { humanizeLookupCollection } from '../BrandPortal/RequestNewValueModal';
import { RequestDetailPanel } from '../BrandPortal/RequestDetailPanel';
import {
  StatusBadge,
  relativeTime,
  requestTitle,
} from '../BrandPortal/MyRequestsPanel';
import {
  OPEN_STATUSES,
  useAllEntityRequests,
} from '../../hooks/useEntityRequestsAdmin';

/**
 * RequestsAdminPage — Brand Portal Phase 2.5 (chunk iv).
 *
 * The admin inbox for entity requests across ALL brands. Lives in the "App
 * Feedback" nav group alongside Pending Approvals. Lists every request live
 * (admins can read the whole collection per the chunk-i rules), with status /
 * type / brand / sort filters applied client-side over the snapshot.
 *
 * Tapping a row opens the chunk-iii `RequestDetailPanel` in `adminMode` — the
 * admin reads the same summary + message thread and can reply. The approve /
 * reject / entity-creation flow is chunk v; this chunk is read + triage only.
 *
 * Brand names and submitter identities are resolved with one-time fetches
 * (`getWatchBrands` / `getAllUsers`) into id→label maps rather than per-row
 * reads — both are admin-cheap and already used elsewhere in the dashboard.
 */

type StatusFilter =
  | 'all'
  | 'open'
  | 'pending'
  | 'needs_info'
  | 'resolved'
  | 'approved'
  | 'rejected';
type TypeFilter = 'all' | 'calibre' | 'lookup';
type SortKey = 'updated' | 'created' | 'status';

const STATUS_FILTERS: { key: StatusFilter; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'open', label: 'Open' },
  { key: 'pending', label: 'Pending' },
  { key: 'needs_info', label: 'Needs info' },
  { key: 'resolved', label: 'Resolved' },
  { key: 'approved', label: 'Approved' },
  { key: 'rejected', label: 'Rejected' },
];

const TYPE_FILTERS: { key: TypeFilter; label: string }[] = [
  { key: 'all', label: 'All types' },
  { key: 'calibre', label: 'Calibre' },
  { key: 'lookup', label: 'Lookup' },
];

const SORTS: { key: SortKey; label: string }[] = [
  { key: 'updated', label: 'Last updated' },
  { key: 'created', label: 'Date created' },
  { key: 'status', label: 'Status' },
];

const RESOLVED_STATUSES: EntityRequest['status'][] = ['approved', 'rejected'];

// Status sort order: open work first, then resolved.
const STATUS_ORDER: Record<EntityRequest['status'], number> = {
  needs_info: 0,
  pending: 1,
  approved: 2,
  rejected: 3,
};

function matchesStatus(status: EntityRequest['status'], filter: StatusFilter): boolean {
  switch (filter) {
    case 'all':
      return true;
    case 'open':
      return OPEN_STATUSES.includes(status);
    case 'resolved':
      return RESOLVED_STATUSES.includes(status);
    default:
      return status === filter;
  }
}

function millis(ts?: { toMillis?: () => number }): number {
  return ts?.toMillis?.() ?? 0;
}

// ── Row ──────────────────────────────────────────────────────────────────────

function RequestRow({
  request,
  brandLabel,
  submitterLabel,
  onSelect,
}: {
  request: EntityRequest;
  brandLabel: string;
  submitterLabel: string;
  onSelect: (request: EntityRequest) => void;
}) {
  // Secondary context line: humanized lookup collection, or calibre maker/type.
  let detail: string;
  if (request.type === 'calibre') {
    const p = request.payload as { manufacturer?: string; movementType?: string };
    const bits = [p.manufacturer, p.movementType].filter(Boolean);
    detail = bits.length ? `Calibre · ${bits.join(' · ')}` : 'Calibre';
  } else {
    const p = request.payload as { collection: string };
    detail = `Lookup · ${humanizeLookupCollection(p.collection)}`;
  }

  return (
    <li>
      <button
        type='button'
        onClick={() => onSelect(request)}
        className='w-full flex items-center gap-4 px-6 py-3 text-left hover:bg-gray-50'
      >
        <StatusBadge status={request.status} />
        <div className='min-w-0 flex-1'>
          <p className='text-sm font-medium text-gray-900 truncate'>
            {requestTitle(request)}
          </p>
          <p className='text-xs text-gray-500 truncate'>{detail}</p>
          <p className='text-xs text-gray-400 truncate'>By {submitterLabel}</p>
        </div>
        <span className='text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full flex-shrink-0 max-w-[12rem] truncate'>
          {brandLabel}
        </span>
        <div className='flex-shrink-0 text-right whitespace-nowrap'>
          <p className='text-xs text-gray-400'>
            Updated {relativeTime(request.updatedAt)}
          </p>
          <p className='text-[11px] text-gray-300'>
            Filed {relativeTime(request.createdAt)}
          </p>
        </div>
      </button>
    </li>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export function RequestsAdminPage() {
  const { requests, loading, error, capped } = useAllEntityRequests();

  const [brandNames, setBrandNames] = useState<Record<string, string>>({});
  const [userLabels, setUserLabels] = useState<Record<string, string>>({});

  const [statusFilter, setStatusFilter] = useState<StatusFilter>('open');
  const [typeFilter, setTypeFilter] = useState<TypeFilter>('all');
  const [brandFilter, setBrandFilter] = useState<string>('all');
  const [sortKey, setSortKey] = useState<SortKey>('updated');

  const [selectedId, setSelectedId] = useState<string | null>(null);

  // One-time identity resolution. Failures degrade to ids (still usable).
  useEffect(() => {
    let active = true;
    getWatchBrands()
      .then((brands) => {
        if (!active) return;
        const map: Record<string, string> = {};
        brands.forEach((b) => {
          if (b.id) map[b.id] = b.name || b.id;
        });
        setBrandNames(map);
      })
      .catch(() => undefined);
    getAllUsers()
      .then((users) => {
        if (!active) return;
        const map: Record<string, string> = {};
        users.forEach((u) => {
          if (u.uid) map[u.uid] = u.displayName || u.email || u.uid;
        });
        setUserLabels(map);
      })
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, []);

  const brandLabelFor = (id: string) => brandNames[id] ?? id;
  const submitterLabelFor = (uid: string) => userLabels[uid] ?? uid;

  // Brands present in the result set, for the brand dropdown.
  const brandOptions = useMemo(() => {
    const ids = Array.from(new Set(requests.map((r) => r.brandId)));
    return ids
      .map((id) => ({ id, label: brandLabelFor(id) }))
      .sort((a, b) => a.label.localeCompare(b.label));
    // brandNames feeds the label; recompute when it lands.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requests, brandNames]);

  const openCount = useMemo(
    () => requests.filter((r) => OPEN_STATUSES.includes(r.status)).length,
    [requests],
  );

  const visible = useMemo(() => {
    const filtered = requests.filter((r) => {
      if (!matchesStatus(r.status, statusFilter)) return false;
      if (typeFilter !== 'all' && r.type !== typeFilter) return false;
      if (brandFilter !== 'all' && r.brandId !== brandFilter) return false;
      return true;
    });
    const sorted = [...filtered];
    if (sortKey === 'updated') {
      sorted.sort((a, b) => millis(b.updatedAt) - millis(a.updatedAt));
    } else if (sortKey === 'created') {
      sorted.sort((a, b) => millis(b.createdAt) - millis(a.createdAt));
    } else {
      sorted.sort(
        (a, b) =>
          STATUS_ORDER[a.status] - STATUS_ORDER[b.status] ||
          millis(b.updatedAt) - millis(a.updatedAt),
      );
    }
    return sorted;
  }, [requests, statusFilter, typeFilter, brandFilter, sortKey]);

  const selected = selectedId
    ? requests.find((r) => r.id === selectedId) ?? null
    : null;

  const selectClass =
    'border border-gray-300 rounded-lg px-2 py-2 text-sm text-gray-700 bg-white focus:ring-2 focus:ring-brand-500 focus:border-brand-500';

  return (
    <div className='bg-white rounded-lg shadow-sm border border-gray-200'>
      <div className='px-6 py-4 border-b border-gray-200'>
        <div className='flex items-center justify-between gap-4'>
          <h2 className='text-base font-semibold text-gray-900'>
            Brand Portal Requests
            {openCount > 0 && (
              <span className='ml-2 text-sm font-normal text-gray-400'>
                · {openCount} open
              </span>
            )}
          </h2>
        </div>

        <div className='mt-3 flex flex-wrap items-center gap-2'>
          {/* Status segmented control */}
          <div className='inline-flex rounded-lg border border-gray-300 overflow-hidden'>
            {STATUS_FILTERS.map((f) => (
              <button
                key={f.key}
                type='button'
                onClick={() => setStatusFilter(f.key)}
                className={`px-3 py-1.5 text-sm border-l first:border-l-0 border-gray-300 ${
                  statusFilter === f.key
                    ? 'bg-brand-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>

          {/* Type */}
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value as TypeFilter)}
            aria-label='Filter by type'
            className={selectClass}
          >
            {TYPE_FILTERS.map((f) => (
              <option key={f.key} value={f.key}>
                {f.label}
              </option>
            ))}
          </select>

          {/* Brand */}
          <select
            value={brandFilter}
            onChange={(e) => setBrandFilter(e.target.value)}
            aria-label='Filter by brand'
            className={selectClass}
          >
            <option value='all'>All brands</option>
            {brandOptions.map((b) => (
              <option key={b.id} value={b.id}>
                {b.label}
              </option>
            ))}
          </select>

          {/* Sort */}
          <select
            value={sortKey}
            onChange={(e) => setSortKey(e.target.value as SortKey)}
            aria-label='Sort by'
            className={`${selectClass} ml-auto`}
          >
            {SORTS.map((s) => (
              <option key={s.key} value={s.key}>
                Sort: {s.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {error ? (
        <div className='px-6 py-10 text-center text-sm text-red-600'>
          Couldn't load requests.{' '}
          <button
            type='button'
            onClick={() => window.location.reload()}
            className='underline font-medium hover:text-red-700'
          >
            Retry
          </button>
        </div>
      ) : loading ? (
        <div className='px-6 py-10 text-center text-sm text-gray-400'>
          Loading requests…
        </div>
      ) : requests.length === 0 ? (
        <div className='px-6 py-10 text-center text-sm text-gray-500'>
          No brand portal requests yet.
        </div>
      ) : visible.length === 0 ? (
        <div className='px-6 py-10 text-center text-sm text-gray-500'>
          No requests match these filters.
        </div>
      ) : (
        <>
          <ul className='divide-y divide-gray-100'>
            {visible.map((req) => (
              <RequestRow
                key={req.id}
                request={req}
                brandLabel={brandLabelFor(req.brandId)}
                submitterLabel={submitterLabelFor(req.submittedBy)}
                onSelect={(r) => setSelectedId(r.id)}
              />
            ))}
          </ul>
          {capped && (
            <div className='px-6 py-3 border-t border-gray-100 text-center text-xs text-gray-400'>
              Showing the 200 most recently updated requests.
            </div>
          )}
        </>
      )}

      {selected && (
        <Modal
          isOpen={true}
          onClose={() => setSelectedId(null)}
          title='Request details'
          size='lg'
        >
          <RequestDetailPanel
            request={selected}
            brandLabel={brandLabelFor(selected.brandId)}
            onClose={() => setSelectedId(null)}
            adminMode
            resolveUserLabel={submitterLabelFor}
          />
        </Modal>
      )}
    </div>
  );
}
